/**
 * Phase FIRST-INV (Part B) — Rental Detail card that owns the manual
 * "Create first invoice" pre-release flow.
 *
 * Visibility: contract fully signed AND rental NOT yet released. Hidden
 * entirely for active / ended / terminated / archived rentals.
 *
 * State machine (per spec §1):
 *   None                     → "Create first invoice" (primary)
 *   draft                    → "Edit first invoice"
 *   sent (unpaid)            → "View · Record payment"
 *   partial_paid             → "View · Record payment"  (shows paid vs total)
 *   paid                     → green "First invoice paid" badge; the release
 *                              CTA in the header row remains the way forward.
 *   voided                   → hidden from lookup, treated as None → allow create
 */
import React, { useCallback, useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, FileText, Pencil, Receipt, DollarSign } from "lucide-react";
import { usePermission } from "@/lib/usePermission";
import FirstInvoiceDraftModal from "@/components/FirstInvoiceDraftModal";
import RecordPaymentModal from "@/components/RecordPaymentModal";

// Pre-release lifecycle states where the manual first-invoice CTA can appear.
// coi_pending / coi_received / coi_approved / contract_signed / imported.
// Explicitly excluded: draft, sent_for_signature (contract not yet signed),
// active / ended / terminated / archived.
const PRE_RELEASE_STATES = new Set([
    "contract_signed",
    "coi_pending",
    "coi_received",
    "coi_approved",
    "imported",
]);

function money(cents) {
    const n = Number(cents || 0) / 100;
    return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function StatusChip({ status }) {
    const map = {
        draft: { label: "Draft", cls: "bg-slate-100 text-slate-700 ring-slate-200" },
        sent: { label: "Issued · unpaid", cls: "bg-amber-50 text-amber-900 ring-amber-200" },
        viewed: { label: "Issued · viewed", cls: "bg-amber-50 text-amber-900 ring-amber-200" },
        overdue: { label: "Issued · overdue", cls: "bg-red-50 text-red-800 ring-red-200" },
        partial_paid: { label: "Partial paid", cls: "bg-blue-50 text-blue-900 ring-blue-200" },
        paid: { label: "Paid", cls: "bg-emerald-50 text-emerald-800 ring-emerald-200" },
        void: { label: "Voided", cls: "bg-slate-200 text-slate-600 ring-slate-300" },
    };
    const m = map[status] || { label: status, cls: "bg-slate-100 text-slate-700 ring-slate-200" };
    return (
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${m.cls}`}
            data-testid={`first-invoice-status-${status}`}>{m.label}</span>
    );
}

export default function FirstInvoiceCard({ rental, onChange }) {
    const { ready, hasPermission } = usePermission();
    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showDraft, setShowDraft] = useState(false);
    const [showPay, setShowPay] = useState(false);

    // Visibility gate — pre-release only.
    const isPreRelease = PRE_RELEASE_STATES.has(rental?.status);

    const load = useCallback(async () => {
        if (!rental?.id || !isPreRelease) {
            setLoading(false);
            setInvoice(null);
            return;
        }
        setLoading(true);
        try {
            // We reuse the standard admin listing endpoint filtered by
            // rental_id — cheaper than a new custom endpoint. Find the
            // non-voided first-invoice row.
            const { data } = await api.get("/admin/invoices", {
                params: { rental_id: rental.id, limit: 25 },
            });
            const items = data?.items || [];
            const active = items.find(
                (i) => i.is_first_invoice && i.status !== "void",
            );
            setInvoice(active || null);
        } catch (err) {
            // Non-fatal — surface but don't blow up the page.
            console.warn("FirstInvoiceCard load failed:", formatApiError(err));
            setInvoice(null);
        } finally {
            setLoading(false);
        }
    }, [rental?.id, isPreRelease]);

    useEffect(() => { load(); }, [load]);

    if (!isPreRelease) return null;

    const canCreate = ready && hasPermission("Invoices", "Create first invoice manually");
    const canEdit = ready && hasPermission("Invoices", "Edit");
    const canRecordPayment = ready && hasPermission("Invoices", "Record payment");

    const refreshAll = async () => {
        await load();
        if (typeof onChange === "function") onChange();
    };

    // Compose the CTA row per the state machine.
    let stateRow = null;
    if (!invoice) {
        stateRow = (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="text-sm text-slate-600">
                    No first invoice yet. You can optionally draft and collect the first
                    invoice (including the security deposit) before releasing equipment.
                </div>
                {canCreate ? (
                    <Button onClick={() => setShowDraft(true)}
                        className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                        data-testid="first-invoice-create-btn">
                        <FileText size={14} /> Create first invoice
                    </Button>
                ) : (
                    <span className="text-xs text-slate-400 italic">
                        (No permission to create first invoice)
                    </span>
                )}
            </div>
        );
    } else if (invoice.status === "draft") {
        stateRow = (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-2 flex-wrap">
                    <StatusChip status="draft" />
                    <span className="text-sm text-slate-700 font-medium">{invoice.invoice_number}</span>
                    <span className="text-sm text-slate-600">· Total {money(invoice.total_cents)}</span>
                </div>
                {canEdit ? (
                    <Button variant="outline" onClick={() => setShowDraft(true)}
                        className="gap-1.5"
                        data-testid="first-invoice-edit-btn">
                        <Pencil size={14} /> Edit first invoice
                    </Button>
                ) : null}
            </div>
        );
    } else if (invoice.status === "paid") {
        stateRow = (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-2 flex-wrap">
                    <StatusChip status="paid" />
                    <span className="text-sm text-slate-700 font-medium">{invoice.invoice_number}</span>
                    <span className="text-sm text-slate-600">· Total {money(invoice.total_cents)}</span>
                </div>
                <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold
                    bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200"
                    data-testid="first-invoice-paid-badge">
                    <CheckCircle2 size={14} /> First invoice paid — equipment can be released
                </span>
            </div>
        );
    } else if (invoice.status === "void") {
        // Should never render — filtered above — but guard defensively.
        stateRow = null;
    } else {
        // sent / viewed / overdue / partial_paid.
        const total = Number(invoice.total_cents || 0);
        const paid = Number(invoice.amount_paid_cents || 0);
        const remaining = Math.max(0, total - paid);
        const label = invoice.status === "partial_paid" ? "Record payment" : "Record payment";
        const StatusIcon = invoice.status === "partial_paid" ? DollarSign : Receipt;
        stateRow = (
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <StatusChip status={invoice.status} />
                        <span className="text-sm text-slate-700 font-medium">{invoice.invoice_number}</span>
                    </div>
                    <div className="text-xs text-slate-600">
                        Paid <span className="font-semibold text-slate-800">{money(paid)}</span>
                        {" / "}
                        <span className="font-semibold text-slate-800">{money(total)}</span>
                        {" · Remaining "}
                        <span className={remaining > 0 ? "font-semibold text-amber-800" : "font-semibold text-emerald-800"}>
                            {money(remaining)}
                        </span>
                    </div>
                </div>
                {canRecordPayment ? (
                    <Button onClick={() => setShowPay(true)}
                        className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                        data-testid="first-invoice-record-payment-btn">
                        <StatusIcon size={14} /> {label}
                    </Button>
                ) : (
                    <span className="text-xs text-slate-400 italic">
                        (No permission to record payment)
                    </span>
                )}
            </div>
        );
    }

    return (
        <Card className="border-slate-200" data-testid="first-invoice-card">
            <CardContent className="p-5 space-y-3">
                <div className="flex items-center gap-2">
                    <Receipt size={16} className="text-blue-900" />
                    <h3 className="text-sm font-semibold text-slate-900">First invoice</h3>
                    <span className="text-[11px] text-slate-500 ml-2">Optional — draft &amp; collect before release</span>
                </div>
                {loading ? (
                    <p className="text-sm text-slate-500">Loading…</p>
                ) : stateRow}
            </CardContent>

            {showDraft ? (
                <FirstInvoiceDraftModal
                    open={showDraft}
                    onOpenChange={setShowDraft}
                    rental={rental}
                    invoice={invoice}
                    onSaved={async () => { setShowDraft(false); await refreshAll(); }}
                />
            ) : null}
            {showPay ? (
                <RecordPaymentModal
                    open={showPay}
                    onOpenChange={setShowPay}
                    invoice={invoice}
                    onRecorded={async () => { setShowPay(false); await refreshAll(); }}
                />
            ) : null}
        </Card>
    );
}
